import { ArrowSquareOut, Phone } from "@phosphor-icons/react";

const CANONICAL=["Google","Trustpilot","Birdeye","BBB","Yelp","Facebook","Angi","Houzz"];
const ALIASES={
  "google reviews":"Google","google maps":"Google","google business profile":"Google","google business profile reviews":"Google",
  "trust pilot":"Trustpilot","better business bureau":"BBB","facebook reviews":"Facebook","facebook recommendations":"Facebook",
  "angies list":"Angi","angie's list":"Angi"
};
const PLATFORM_DOMAINS={
  Google:"google.com",Trustpilot:"trustpilot.com",Birdeye:"birdeye.com",BBB:"bbb.org",
  Yelp:"yelp.com",Facebook:"facebook.com",Angi:"angi.com",Houzz:"houzz.com"
};

export function canonicalReviewSources(items=[]){
  const byKey=new Map();
  for(const rawItem of items||[]){
    if(!rawItem)continue;
    const raw=String(rawItem.platform||"").trim();
    const canonical=ALIASES[raw.toLowerCase()]||CANONICAL.find(x=>x.toLowerCase()===raw.toLowerCase());
    if(!canonical)continue;
    const item={...rawItem,platform:canonical};
    const current=byKey.get(canonical);
    const score=(x)=>(x?.url?4:0)+(x?.rating!=null?2:0)+(x?.review_count!=null?1:0)+(x?.verification_status==="verified_absent"?3:0);
    if(!current||score(item)>score(current))byKey.set(canonical,item);
  }
  return CANONICAL.map(platform=>byKey.get(platform)||{platform,verification_status:"verification_failed"});
}

function allReviewSources(items=[]){
  const canonical=canonicalReviewSources(items);
  const canonicalKeys=new Set(CANONICAL.map(x=>x.toLowerCase()));
  const extras=[];
  const seen=new Set();
  for(const item of items||[]){
    if(!item?.platform||!item?.url)continue;
    const raw=String(item.platform).trim();
    const normalized=ALIASES[raw.toLowerCase()]||raw;
    if(canonicalKeys.has(normalized.toLowerCase()))continue;
    const key=normalized.toLowerCase()+"|"+item.url;
    if(seen.has(key))continue;
    seen.add(key);
    extras.push({...item,platform:normalized,verification_status:item.verification_status||"verified_profile"});
  }
  return [...canonical,...extras];
}

const resourceUrl=(r)=>r?.direct_url||r?.url||"";
const resourceLabel=(r)=>{
  const t=String(r?.type||"").toLowerCase();
  if(t==="financing")return "financing options";
  if(t==="estimate")return "request an estimate";
  if(t==="scheduling")return "schedule service";
  if(t==="warranty")return "warranty information";
  if(t==="membership")return "maintenance plan";
  if(t==="emergency_service")return "emergency service";
  if(t==="rebates")return "rebates";
  if(t==="payment_options")return "payment options";
  if(t==="service_area")return "service area";
  return String(r?.label||"").trim();
};
const norm=(v)=>String(v||"").toLowerCase().replace(/[^a-z0-9]+/g," ").trim();
const serviceResourceFor=(service,resources=[])=>{
  const s=norm(service);
  if(!s)return null;
  const serviceResources=(resources||[]).filter(r=>String(r?.type||"").toLowerCase()==="service"&&resourceUrl(r));
  let best=null,bestScore=0;
  for(const r of serviceResources){
    const l=norm(r.label);
    if(!l)continue;
    let score=0;
    if(s===l)score=100;
    else if(s.includes(l)||l.includes(s))score=70;
    else{
      const a=new Set(s.split(" ").filter(x=>x.length>2));
      const b=new Set(l.split(" ").filter(x=>x.length>2));
      const overlap=[...a].filter(x=>b.has(x)).length;
      score=overlap*12;
    }
    if(score>bestScore){best=r;bestScore=score;}
  }
  return bestScore>=12?best:null;
};

function buildLinkRules(profile){
  const rules=[];
  const resources=[...(profile?.buyer_resources||[]),...(profile?.service_resources||[])];
  for(const src of (profile?.editorial_sources||[])){
    try{
      const u=new URL(src);
      const rawPath=decodeURIComponent(u.pathname||"").replace(/[-_]+/g," ").replace(/\/+$/,"").trim();
      if(!rawPath||rawPath==="/")continue;
      const segment=rawPath.split("/").filter(Boolean).pop()||"";
      const label=segment.replace(/\b\w/g,ch=>ch.toUpperCase());
      const hay=rawPath.toLowerCase();
      const isService=/hvac|air conditioning|heating|furnace|heat pump|plumbing|drain|jetting|water heater|indoor air|commercial|industrial|repair|installation|replacement|thermostat|duct/.test(hay);
      if(isService&&!resources.some(r=>resourceUrl(r)===src))resources.push({type:"service",label,url:src});
    }catch{}
  }
  for(const r of resources){
    const url=resourceUrl(r),label=resourceLabel(r);
    if(url&&label)rules.push({label,url});
    const t=String(r?.type||"").toLowerCase();
    if(url&&t==="financing")rules.push({label:"financing",url});
    if(url&&t==="membership")rules.push({label:"maintenance plans",url});
    if(url&&t==="emergency_service")rules.push({label:"24/7 emergency service",url});
    if(url&&t==="scheduling")rules.push({label:"schedule service",url});
    if(url&&t==="estimate")rules.push({label:"request an estimate",url});
    if(url&&t==="warranty")rules.push({label:"warranties",url});
  }
  for(const service of (profile?.services||[])){
    const matched=serviceResourceFor(service,resources);
    if(matched&&resourceUrl(matched))rules.push({label:String(service),url:resourceUrl(matched)});
  }
  for(const source of allReviewSources(profile?.reputation_sources||[])){
    if(source.url)rules.push({label:source.platform==="Google"?"Google Reviews":source.platform,url:source.url});
  }
  const seen=new Set();
  return rules.filter(r=>{
    const k=r.label.toLowerCase()+"|"+r.url;
    if(seen.has(k))return false;
    seen.add(k);return true;
  }).sort((a,b)=>b.label.length-a.label.length);
}

function LinkedText({text,profile}){
  const value=String(text||"");
  if(!value)return null;
  const rules=buildLinkRules(profile);
  const lower=value.toLowerCase();
  const hits=[];
  const usedUrls=new Set();
  for(const rule of rules){
    const needle=rule.label.toLowerCase();
    const at=lower.indexOf(needle);
    if(at>=0&&!usedUrls.has(rule.url)){
      hits.push({...rule,start:at,end:at+needle.length});
      usedUrls.add(rule.url);
    }
  }
  hits.sort((a,b)=>a.start-b.start);
  const clean=[];let cursor=-1;
  for(const hit of hits){
    if(clean.length>=3)break;
    if(hit.start<cursor)continue;
    const previous=clean[clean.length-1];
    if(previous && hit.start-previous.end<24)continue;
    clean.push(hit);
    cursor=hit.end;
  }
  if(!clean.length)return value;
  const out=[];let pos=0;
  clean.forEach((hit,i)=>{
    if(hit.start>pos)out.push(value.slice(pos,hit.start));
    out.push(<a key={hit.url+"-"+i} href={hit.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#2D5BFF] hover:underline">{value.slice(hit.start,hit.end)}</a>);
    pos=hit.end;
  });
  if(pos<value.length)out.push(value.slice(pos));
  return <>{out}</>;
}

function ReviewParagraphs({text,profile,className=""}){
  const parts=String(text||"").split(/\n\s*\n+/).map(x=>x.trim()).filter(Boolean);
  if(!parts.length)return <p className={className}><LinkedText text="—" profile={profile}/></p>;
  return <div className={"space-y-4 "+className}>{parts.map((part,i)=><p key={i}><LinkedText text={part} profile={profile}/></p>)}</div>;
}

function Section({id,title,children,className=""}){
  return <section id={id} className={"mt-12 "+className}><h2 className="text-2xl sm:text-[28px] leading-tight font-black text-zinc-900">{title}</h2>{children}</section>;
}

function PlatformCard({source,preview}){
  let domain=PLATFORM_DOMAINS[source.platform]||"";
  if(!domain&&source.url){
    try{domain=new URL(source.url).hostname.replace(/^www\./,"");}catch{}
  }
  const icon=domain?"https://www.google.com/s2/favicons?domain="+domain+"&sz=64":"";
  const verified=source.verification_status==="verified_profile";
  const absent=source.verification_status==="verified_absent";
  return <div className="rounded-xl border border-zinc-200 bg-white p-4 min-h-[150px]">
    <div className="flex items-center gap-3">
      {icon?<img src={icon} alt="" className="w-8 h-8 rounded-md" loading="lazy"/>:null}
      <div><h3 className="font-black text-lg">{source.platform==="Google"?"Google Reviews":source.platform}</h3><p className="text-xs text-zinc-500">Independent reputation source</p></div>
    </div>
    {verified&&source.rating!=null?<div className="mt-3 flex flex-wrap items-center gap-2 text-sm"><span className="text-amber-500 tracking-wide">★★★★★</span><strong>{source.rating}/5</strong>{source.review_count!=null?<span className="text-zinc-500">({source.review_count} reviews)</span>:null}</div>:null}
    {verified&&source.rating==null&&source.review_count!=null?<p className="mt-3 text-sm text-zinc-700"><strong>{source.review_count}</strong> reviews listed</p>:null}
    {verified&&source.url?<a href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#2D5BFF] hover:underline">View {source.platform==="Google"?"Google reviews":source.platform+" profile"} <ArrowSquareOut size={14}/></a>:null}
    {absent?<p className="mt-3 text-sm font-semibold text-zinc-600">Currently no reviews listed</p>:null}
    
  </div>;
}

export default function TrustProfileReview({profile={},preview=false}){
  const sources=allReviewSources(profile.reputation_sources||[]);
  const rawServices=Array.isArray(profile.services)?profile.services:[];
  const normalizedServices=[];
  const seenServices=new Set();
  for(const raw of rawServices){
    const display=String(raw||"").replace(/\s*\((?:as\s+)?marketed(?:\s+by\s+the\s+company)?\)\s*/ig," ").replace(/\s+/g," ").trim();
    const key=norm(display).replace(/\bwith approved credit\b/g,"").trim();
    if(!display||!key||seenServices.has(key))continue;
    seenServices.add(key);
    normalizedServices.push(display);
  }
  const services=normalizedServices;
  const resources=[...(profile.buyer_resources||[]),...(profile.service_resources||[])];
  const lastReviewed=profile.last_reviewed_at||profile.published_at||new Date().toISOString();
  const estimateUrl=profile.request_estimate_url||profile.website;
  const scheduleUrl=profile.schedule_service_url||"";

  return <article className="bg-white text-zinc-900" style={{fontFamily:"Manrope,system-ui,sans-serif"}}>
    <div className="border border-zinc-200 rounded-2xl overflow-hidden bg-white">
      <header className="p-6 sm:p-9 border-b border-zinc-200">
        {preview?<p className="text-xs font-black tracking-[.18em] uppercase text-[#2D5BFF]">Pre-Publish Preview</p>:null}
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-2 max-w-4xl">{profile.name} Review</h1>
        <p className="mt-3 text-lg text-zinc-600">{[profile.category,profile.city,profile.state].filter(Boolean).join(" · ")}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-500">
          <span><strong className="text-zinc-800">Written, researched & published by:</strong> RankTrust.io Editorial</span>
          <span>Verified business research, reputation & trust analysis, buyer guidance.</span>
          <span><strong className="text-zinc-800">Last reviewed:</strong> {new Date(lastReviewed).toLocaleDateString()}</span>
        </div>
        <div className="flex flex-wrap gap-3 mt-6">
          {profile.website?<a href={profile.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-[#2D5BFF] text-white px-5 py-3 font-extrabold">Visit Official Website <ArrowSquareOut size={16}/></a>:null}
          {profile.phone?<a href={"tel:"+String(profile.phone).replace(/[^+\d]/g,"")} className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 px-5 py-3 font-extrabold">Call Now <Phone size={16}/></a>:null}
          {estimateUrl?<a href={estimateUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 px-5 py-3 font-extrabold">Request an Estimate</a>:null}
          {scheduleUrl?<a href={scheduleUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 px-5 py-3 font-extrabold">Schedule Service</a>:null}
        </div>
      </header>

      <div className="p-6 sm:p-9">
        <section className="max-w-4xl">
          <h2 className="text-2xl font-black text-zinc-900">{profile.name} At A Glance</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[15px] sm:text-base text-zinc-700 list-disc pl-5">{services.slice(0,6).map((x,i)=><li key={i}>{x}</li>)}</ul>
        </section>

        <section className="grid md:grid-cols-2 gap-6 mt-8 max-w-4xl">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
            <h2 className="text-xl font-black text-zinc-900">Strengths</h2>
            <ReviewParagraphs text={profile.what_stands_out||"—"} profile={profile} className="mt-3 text-[15px] sm:text-base text-zinc-700 leading-7"/>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-5">
            <h2 className="text-xl font-black text-zinc-900">Considerations</h2>
            <ReviewParagraphs text={profile.customer_concerns||profile.customer_should_know||"—"} profile={profile} className="mt-3 text-[15px] sm:text-base text-zinc-700 leading-7"/>
          </div>
        </section>

        <Section id="rt-overview" title={"RankTrust Review of "+profile.name}>
          <ReviewParagraphs text={profile.editorial_overview||"—"} profile={profile} className="mt-5 text-[16px] sm:text-[17px] text-zinc-700 leading-7 max-w-3xl"/>
          {(profile.editorial_sections||[]).length?<div className="mt-10 space-y-10 max-w-3xl">
            {(profile.editorial_sections||[]).map((section,i)=><section key={i} className="border-t border-zinc-200 pt-7">
              <h3 className="text-xl sm:text-[22px] leading-snug font-black text-zinc-900">{section.heading}</h3>
              <ReviewParagraphs text={section.body} profile={profile} className="mt-4 text-[16px] sm:text-[17px] text-zinc-700 leading-7"/>
            </section>)}
          </div>:null}
        </Section>

        <Section id="rt-sources" title="Reputation Sources">
          <p className="mt-3 text-zinc-600 max-w-4xl">RankTrust checks major reputation platforms individually and also includes additional business-specific reputation sources when RankTrust can verify a direct profile for the exact business. Verified profiles link directly to the listing.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">{sources.map(source=><PlatformCard key={source.platform} source={source} preview={preview}/>)}</div>
        </Section>

        <section className="mt-10 pb-8 border-b border-zinc-200">
          <h2 className="text-2xl font-black text-[#2D5BFF]">Table Of Contents</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[#2D5BFF] font-bold">
            <a href="#rt-overview">RankTrust Review</a><a href="#rt-sources">Reputation Sources</a><a href="#rt-reputation">Customer Reputation & Reviews</a><a href="#rt-services">Services & Specialties</a><a href="#rt-feedback">Positive Customer Feedback</a><a href="#rt-concerns">Complaints & Concerns</a><a href="#rt-verified">Verified Business Information</a><a href="#rt-buyer-guidance">Buyer Guidance</a><a href="#rt-questions">Questions To Ask Before Hiring</a><a href="#rt-verdict">RankTrust Editorial Assessment</a>
          </div>
        </section>

        <Section id="rt-reputation" title="Customer Reputation & Review Analysis">
          <p className="mt-4 text-zinc-700 whitespace-pre-wrap leading-7 max-w-4xl"><LinkedText text={profile.customer_feedback_summary||"—"} profile={profile}/></p>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">{sources.filter(x=>x.url).map(x=><a key={x.platform} href={x.url} target="_blank" rel="noopener noreferrer" className="font-bold text-[#2D5BFF] hover:underline">{x.platform==="Google"?"Google Reviews":x.platform} →</a>)}</div>
        </Section>

        <Section id="rt-services" title="Services & Specialties">
          <div className="mt-4 grid sm:grid-cols-2 gap-3">{services.map((service,i)=>{
            const r=serviceResourceFor(service,resources);
            return r?<a key={i} href={resourceUrl(r)} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 font-semibold text-[#2D5BFF] hover:border-[#2D5BFF]/40 hover:bg-blue-50">{service} <ArrowSquareOut size={14} className="inline"/></a>:<div key={i} className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 font-semibold">{service}</div>;
          })}</div>
        </Section>

        <Section title="What Customers Should Know"><p className="mt-4 text-zinc-700 whitespace-pre-wrap leading-7 max-w-4xl"><LinkedText text={profile.customer_should_know||"—"} profile={profile}/></p></Section>
        <Section id="rt-feedback" title="Positive Customer Feedback Themes"><p className="mt-4 text-zinc-700 whitespace-pre-wrap leading-7 max-w-4xl"><LinkedText text={profile.positive_feedback_themes||"—"} profile={profile}/></p></Section>
        <Section id="rt-concerns" title="Complaints Or Concerns"><div className="mt-4 border-l-4 border-amber-400 bg-amber-50 p-5 rounded-r-lg"><p className="text-zinc-800 whitespace-pre-wrap leading-7"><LinkedText text={profile.customer_concerns||"No material recurring concerns documented in the current evidence."} profile={profile}/></p></div></Section>

        <Section id="rt-verified" title="Business Information RankTrust Verified">
          <div className="mt-4 overflow-hidden rounded-lg border border-zinc-200 text-sm">
            <div className="grid sm:grid-cols-2 bg-zinc-50 border-b border-zinc-200"><div className="p-3 font-bold">Official website</div><div className="p-3">{profile.website?<a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-[#2D5BFF] font-semibold hover:underline">{profile.website}</a>:"—"}</div></div>
            <div className="grid sm:grid-cols-2 border-b border-zinc-200"><div className="p-3 font-bold">Public phone</div><div className="p-3">{profile.phone||"—"}</div></div>
            <div className="grid sm:grid-cols-2 bg-zinc-50 border-b border-zinc-200"><div className="p-3 font-bold">Business category</div><div className="p-3">{profile.category||"—"}</div></div>
            <div className="grid sm:grid-cols-2"><div className="p-3 font-bold">Primary market</div><div className="p-3">{[profile.city,profile.state].filter(Boolean).join(", ")||"—"}</div></div>
          </div>
        </Section>

        <Section title="What RankTrust Could Not Independently Verify"><p className="mt-4 text-zinc-700 whitespace-pre-wrap leading-7 max-w-4xl">{profile.verification_limits||"—"}</p></Section>

        <Section id="rt-buyer-guidance" title="Buyer Guidance">
          <div className="mt-4 border-l-4 border-[#2D5BFF] bg-blue-50 p-5 rounded-r-lg"><p className="text-zinc-800 whitespace-pre-wrap leading-7"><LinkedText text={profile.buyer_guidance||"Buyer guidance will be generated from the business category, services and available evidence."} profile={profile}/></p></div>
          {(profile.buyer_resources||[]).length?<div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">{(profile.buyer_resources||[]).filter(r=>resourceUrl(r)&&String(r?.type||"").toLowerCase()!=="service").map((r,i)=><a key={i} href={resourceUrl(r)} target="_blank" rel="noopener noreferrer" className="font-bold text-[#2D5BFF] hover:underline">{resourceLabel(r)||"Learn more"} →</a>)}</div>:null}
        </Section>

        <Section id="rt-questions" title="Questions To Ask Before Hiring"><ol className="mt-5 space-y-4 max-w-4xl">{(profile.customer_questions||[]).map((q,i)=><li key={i} className="flex gap-4"><span className="w-8 h-8 shrink-0 rounded-full bg-[#2D5BFF] text-white flex items-center justify-center font-black">{i+1}</span><p className="pt-1 text-zinc-700">{q}</p></li>)}</ol></Section>
        <Section id="rt-verdict" title="RankTrust Editorial Assessment"><p className="mt-4 text-zinc-700 whitespace-pre-wrap leading-8 max-w-4xl"><LinkedText text={profile.editorial_assessment||"—"} profile={profile}/></p></Section>

        {profile.aggregate_rating||((profile.reviews||[]).length)?<Section title="RankTrust Customer Reviews">
          {profile.aggregate_rating?<div className="mt-4 flex items-center gap-3 flex-wrap"><span className="text-amber-500 text-xl">★★★★★</span><strong>{profile.aggregate_rating.ratingValue}/5</strong><span className="text-zinc-500">({profile.aggregate_rating.reviewCount} RankTrust reviews)</span></div>:null}
          {(profile.reviews||[]).map(review=><div key={review.id} className="mt-4 rounded-lg border border-zinc-200 p-4"><div className="flex justify-between gap-3"><strong>{review.reviewer_name||"RankTrust reviewer"}</strong><span className="text-amber-500">{"★".repeat(review.rating)}{"☆".repeat(5-review.rating)}</span></div>{review.comment?<p className="mt-3 text-zinc-700">{review.comment}</p>:null}</div>)}
        </Section>:null}

        <section className="mt-10 border-t border-zinc-200 pt-8">
          <h2 className="text-2xl font-black text-[#2D5BFF]">Sources & Methodology</h2>
          <p className="mt-3 text-sm text-zinc-600 max-w-3xl">RankTrust.io reviews owner-submitted information, the business's official website, available reputation sources and decision-useful public evidence. Editorial conclusions are limited to evidence available at the time of review.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(profile.editorial_sources||[]).map((src,i)=>{
              let label="Verified source";
              try{
                const u=new URL(src);
                const segment=decodeURIComponent(u.pathname||"").split("/").filter(Boolean).pop()||u.hostname;
                label=segment.replace(/[-_]+/g," ").replace(/\b\w/g,ch=>ch.toUpperCase());
                if(!segment||segment===u.hostname)label=u.hostname.replace(/^www\./,"");
              }catch{}
              return <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-[#2D5BFF] font-bold hover:bg-blue-50">{label} <ArrowSquareOut size={13}/></a>;
            })}
          </div>
        </section>

        <div className="mt-10 border-t border-zinc-200 pt-7 flex flex-wrap gap-3">
          {estimateUrl?<a href={estimateUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-[#2D5BFF] text-white px-5 py-3 font-extrabold">Request an Estimate <ArrowSquareOut size={16}/></a>:null}
          {profile.phone?<a href={"tel:"+String(profile.phone).replace(/[^+\d]/g,"")} className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 px-5 py-3 font-extrabold">Call Now <Phone size={16}/></a>:null}
          {scheduleUrl?<a href={scheduleUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 px-5 py-3 font-extrabold">Schedule Service</a>:null}
        </div>
      </div>
    </div>
  </article>;
}
